import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Home, Briefcase, Info, HelpCircle, Mail, User, Settings, LifeBuoy, Layers, GitBranch } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ScrollProgress from './ScrollProgress';
import AccessibilityPanel from './AccessibilityPanel';

export const NavbarHTAG = () => {
    // Fonction pour gérer le clic sur le logo
    const handleLogoClick = () => {
      setIsOpen(false);
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
    };
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isParticuliers = location.pathname.startsWith('/particuliers');

  // Menu particuliers
  const navItemsParticuliers = [
    { label: 'Accueil', id: 'accueil', icon: <Home size={22} /> },
    { label: 'Services', id: 'services', icon: <Settings size={22} /> },
    { label: 'FAQ', id: 'faq', icon: <HelpCircle size={22} /> },
    { label: 'Contact', id: 'contact', icon: <Mail size={22} /> },
  ];
  // Menu pro
  const navItemsPro = [
    { label: 'Assistance numérique', id: 'assistance', icon: <LifeBuoy size={22} /> },
    { label: 'Nos services', id: 'services', icon: <Settings size={22} /> },
    { label: 'Portfolio', id: 'portfolio', icon: <Briefcase size={22} /> },
    { label: 'Nos créations', id: 'creatives', icon: <Layers size={22} /> },
    { label: 'À propos', id: 'apropos', icon: <Info size={22} /> },
    { label: 'FAQ', id: 'faq', icon: <HelpCircle size={22} /> },
    { label: 'Contact', id: 'contact', icon: <Mail size={22} /> },
  ];
  const navItems = isParticuliers ? navItemsParticuliers : navItemsPro;
  // État d'extension de la barre latérale desktop
  const [expanded, setExpanded] = useState(false);


  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id) => {
    setIsOpen(false);
    const scrollToId = () => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    // Pro page: scroll locally on "/", otherwise redirect to home with hash
    if (!isParticuliers) {
      if (location.pathname === '/') {
        setTimeout(scrollToId, 0);
      } else {
        navigate(`/#${id}`);
      }
      return;
    }

    // Particuliers page: scroll locally on "/particuliers*", otherwise redirect to particuliers with hash
    if (isParticuliers) {
      if (location.pathname.startsWith('/particuliers')) {
        setTimeout(scrollToId, 0);
      } else {
        navigate(`/particuliers#${id}`);
      }
    }
  };

  return (
    <>
      <ScrollProgress />

      {/* Barre latérale desktop : se déploie au survol pour afficher le texte */}
      <motion.aside
        initial={{ x: -80, opacity: 0 }}
        animate={{ x: 0, opacity: 1, width: expanded ? 256 : 64 }}
        transition={{ duration: 0.42, ease: [0.22, 0.61, 0.36, 1] }}
        className="hidden lg:flex fixed top-0 left-0 h-screen z-50 flex-col justify-between bg-black/90 border-r border-white/10 backdrop-blur-xl px-4"
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocusCapture={() => setExpanded(true)}
      >
        <div className="flex flex-col w-full">
          <button
            onClick={handleLogoClick}
            className={`w-full h-16 flex items-center ${expanded ? 'justify-start gap-3 pl-2' : 'justify-center'} hover:bg-white/5 transition-colors duration-300`}
            aria-label="Revenir en haut"
          >
            <img src="/logo.png" alt="HTAG Facility Logo" className="h-10 w-10" />
            {expanded && <span className="text-white font-semibold text-sm tracking-tight">HTAG Facility</span>}
          </button>
          <div className={`mt-4 flex-1 flex flex-col ${expanded ? 'items-start' : 'items-center'} gap-2`}>
            {navItems.map((item, index) => (
              <div key={index} className="relative w-full group">
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={`w-full h-12 flex items-center ${expanded ? 'justify-start px-3 gap-3' : 'justify-center'} rounded-2xl hover:bg-white/10 text-white/70 hover:text-white transition-colors duration-300`}
                  aria-label={item.label}
                >
                  {!expanded && item.icon}
                  {expanded && (
                    <motion.span
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -6 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                      className="text-sm font-medium tracking-tight"
                      style={{ color: '#f5f5f7' }}
                    >
                      {item.label}
                    </motion.span>
                  )}
                </button>
                {!expanded && (
                  <span
                    className="pointer-events-none absolute left-14 top-1/2 -translate-y-1/2 whitespace-nowrap bg-white/10 text-white text-xs font-medium px-3 py-1 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                  >
                    {item.label}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 mb-6">
          <div className="relative w-full group">
            <button
              className={`w-full h-12 flex items-center ${expanded ? 'justify-start px-3 gap-3' : 'justify-center'} rounded-2xl bg-white/10 text-white hover:bg-white/20 transition-colors duration-300`}
              onClick={() => navigate(isParticuliers ? '/' : '/particuliers')}
              aria-label={isParticuliers ? 'Aller vers Professionnels' : 'Aller vers Particuliers'}
            >
              {!expanded && <User size={20} />}
              {expanded && (
                <motion.span
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="text-sm font-medium"
                  style={{ color: '#f5f5f7' }}
                >
                  {isParticuliers ? 'Professionnels' : 'Particuliers'}
                </motion.span>
              )}
            </button>
            {!expanded && (
              <span
                className="pointer-events-none absolute left-14 top-1/2 -translate-y-1/2 whitespace-nowrap bg-white/10 text-white text-xs font-medium px-3 py-1 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              >
                {isParticuliers ? 'Professionnels' : 'Particuliers'}
              </span>
            )}
          </div>
          <div className="relative w-full group">
            <button
              className={`w-full h-12 flex items-center ${expanded ? 'justify-start px-3 gap-3' : 'justify-center'} rounded-2xl hover:bg-white/10 text-white/70 hover:text-white transition-colors duration-300`}
              onClick={() => setIsOpen((prev) => !prev)}
              aria-label="Ouvrir le menu mobile"
            >
              {!expanded && <Menu size={20} />}
              {expanded && (
                <motion.span
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="text-sm font-medium"
                  style={{ color: '#f5f5f7' }}
                >
                  Menu
                </motion.span>
              )}
            </button>
            {!expanded && (
              <span
                className="pointer-events-none absolute left-14 top-1/2 -translate-y-1/2 whitespace-nowrap bg-white/10 text-white text-xs font-medium px-3 py-1 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              >
                Menu
              </span>
            )}
          </div>
        </div>
      </motion.aside>

      {/* Navbar mobile/tablette classique */}
      <motion.nav
        className={`apple-header fixed top-0 w-full z-50 transition-all duration-300 lg:hidden`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo */}
          <motion.button
            onClick={handleLogoClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center space-x-3 cursor-pointer transition-opacity"
          >
            <img src="/logo.png" alt="HTAG Facility Logo" className="h-12 w-auto" />
          </motion.button>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-white p-2 hover:bg-white/5 rounded-lg transition-all"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {/* Mobile Menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="apple-glass-dark border-b border-white/5"
          >
            <div className="container mx-auto px-6 py-6 space-y-2">
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavClick(item.id)}
                  className="block w-full text-left py-3 px-4 rounded-lg hover:bg-white/5 transition-all cursor-pointer"
                  style={{ color: '#f5f5f7', fontSize: '15px' }}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => handleNavClick('contact')}
                className="block w-full apple-button mt-4 cursor-pointer"
              >
                Devis gratuit
              </button>
              <div className="flex gap-2 mt-4">
                <button
                  className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg"
                  onClick={() => navigate('/')}
                >
                  Professionnels
                </button>
                <button
                  className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 border border-white/20 text-white bg-transparent hover:bg-white/10"
                  onClick={() => navigate('/particuliers')}
                >
                  Particuliers
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </motion.nav>
    </>
  );
};

export default NavbarHTAG;
